import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { statusLabel } from './rebate-settlement.utils';
import { getSettlementPayoutDate } from './settlement.utils';
import { UnifiedMonthlyRow, UnifiedTotals } from './unified-settlement.utils';

// ─────────────────────────────────────────────
// 통합 정산(월별 합계) 엑셀 다운로드
//
// 화면의 월별 표와 같은 컬럼·같은 금액으로 1행 = 1정산월.
// 프로젝트 표준(커뮤니티 xlsx + file-saver)만 사용하고, 가독성은 열 너비·행 높이·
// 숫자 포맷·헤더 고정으로 확보한다(셀 스타일 미지원 빌드).
// ─────────────────────────────────────────────

const INT_FMT = '#,##0';
const HEADER_ROW_PT = 28;
const BODY_ROW_PT = 22;
const MONEY_LABELS = [
  '배송형 정산금',
  '쿠폰형 정산금',
  '합계',
  '상조 정산금①',
  '마리 수익금 1%②',
  '계산서 총액①+②',
];

const fmtD = (d?: Date): string => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
};

/**
 * 분할 지급(차수) 내역 — "배송형 1차 100,000 / 배송형 2차 30,640".
 * 차수가 갈린 경우에만 채운다(여러 상조가 접힌 행은 대상 아님 — seq는 모두 1).
 */
const splitNote = (r: UnifiedMonthlyRow): string => {
  const parts: string[] = [];
  (
    [
      ['배송형', r.delivery],
      ['쿠폰형', r.coupon],
    ] as const
  ).forEach(([base, side]) => {
    if (!side || !side.docs.some((d) => d.seq > 1)) return;
    side.docs.forEach((d) =>
      parts.push(`${base} ${d.seq}차 ${d.amount.toLocaleString('ko-KR')}`),
    );
  });
  return parts.join(' / ');
};

/** 상태 표기 — 문서가 없는 달은 빈칸 */
const sideStatus = (
  status: GiftSettlementStatus | undefined,
  label: (s: GiftSettlementStatus) => string,
): string => (status ? label(status) : '');

/** 열 너비 자동 조정 (한글 2.2칸 / 영문 1칸, 금액은 콤마 폭 보정) */
function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 8;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v == null) continue;
      let text = String(cell.v);
      if (typeof cell.v === 'number') {
        const digits = Math.abs(Math.trunc(cell.v)).toString().length;
        text += ','.repeat(Math.max(0, Math.floor((digits - 1) / 3)));
      }
      let w = 0;
      for (const ch of text) w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
      if (w > max) max = w;
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 4, 40) });
  }
  ws['!cols'] = cols;
}

/** 금액 컬럼에 #,##0 포맷 + 행 높이 + 헤더 고정 */
function finishSheet(ws: XLSX.WorkSheet) {
  autoWidth(ws);

  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const rows: XLSX.RowInfo[] = [];
  for (let R = range.s.r; R <= range.e.r; R++) {
    rows[R] = { hpt: R === 0 ? HEADER_ROW_PT : BODY_ROW_PT };
  }
  ws['!rows'] = rows;

  const moneyCols: number[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    const hdr = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (hdr && MONEY_LABELS.includes(String(hdr.v))) moneyCols.push(C);
  }
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    for (const C of moneyCols) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && typeof cell.v === 'number') cell.z = INT_FMT;
    }
  }

  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (!ws['!views']) ws['!views'] = [];
  (ws['!views'] as Array<Record<string, unknown>>)[0] = { state: 'frozen', ySplit: 1 };
}

/** 통합 정산 엑셀 옵션 */
export interface UnifiedExcelOptions {
  /** 상조 칼럼 포함 (관리자·마리 view — 행이 상조 단위) */
  withBrand?: boolean;
  /** 마리 경유 칼럼 포함 (관리자 view 전용) */
  withViaMari?: boolean;
  /**
   * 마리 수익금(1%)·마리 포함 총액 칼럼 포함 — **admin·마리 view 전용**.
   * 상조에게 나가는 파일에는 절대 켜지 않는다.
   */
  withMariShare?: boolean;
  /** 상태 라벨 — 화면 계열별 카피에 맞춘다 (기본 '정산예정'/'지급완료') */
  statusLabelFn?: (s: GiftSettlementStatus) => string;
}

/**
 * 월별 통합 정산 표를 엑셀로 내보낸다 (화면 표와 동일한 행·합계).
 *
 * @param rows     화면에 보이는 행 (필터 적용 후)
 * @param totals   화면 합계 행과 동일한 합계
 * @param fileName 저장 파일명 (.xlsx 포함)
 * @param options  상조 칼럼·상태 라벨 등 화면 계열별 차이
 */
export function exportUnifiedSettlementExcel(
  rows: UnifiedMonthlyRow[],
  totals: UnifiedTotals,
  fileName = '정산합계.xlsx',
  options: UnifiedExcelOptions = {},
) {
  const {
    withBrand = false,
    withViaMari = false,
    withMariShare = false,
    statusLabelFn = statusLabel,
  } = options;
  const wb = XLSX.utils.book_new();

  const brandCols = (r?: UnifiedMonthlyRow) =>
    withBrand
      ? {
          상조: r ? r.brandName ?? '-' : '',
          ...(withViaMari
            ? {
                경유: r
                  ? r.viaMari === undefined
                    ? ''
                    : r.viaMari
                    ? '마리 경유'
                    : '가온 직접'
                  : '',
              }
            : {}),
        }
      : {};

  const sheetRows: Record<string, unknown>[] = rows.map((r) => ({
    정산월: `${r.year}-${String(r.month).padStart(2, '0')}`,
    ...brandCols(r),
    '배송형 정산금': r.delivery ? r.delivery.amount : '',
    '배송형 상태': sideStatus(r.delivery?.status, statusLabelFn),
    '쿠폰형 정산금': r.coupon ? r.coupon.amount : '',
    '쿠폰형 상태': sideStatus(r.coupon?.status, statusLabelFn),
    // 마리 분배분은 상조 정산금과 별개 — 합계엔 안 섞고 ①/②/①+②로 나눠 쓴다
    ...(withMariShare
      ? {
          '상조 정산금①': r.totalAmount,
          '마리 수익금 1%②': r.mariShare > 0 ? r.mariShare : '',
          '계산서 총액①+②': r.totalAmount + r.mariShare,
          세금구분: '과세(부가세 포함)',
        }
      : { 합계: r.totalAmount }),
    // 화면과 동일하게 미지급 건은 정산월 기준 예정일(다음달 15일)로 채운다
    '배송형 지급(예정)일': r.delivery
      ? fmtD(r.delivery.executedAt ?? getSettlementPayoutDate(r.year, r.month))
      : '',
    '쿠폰형 지급(예정)일': r.coupon
      ? fmtD(r.coupon.executedAt ?? getSettlementPayoutDate(r.year, r.month))
      : '',
    분할내역: splitNote(r),
  }));

  sheetRows.push({
    정산월: '■ 합계',
    ...brandCols(),
    '배송형 정산금': totals.deliveryAmount,
    '배송형 상태': '',
    '쿠폰형 정산금': totals.couponAmount,
    '쿠폰형 상태': '',
    ...(withMariShare
      ? {
          '상조 정산금①': totals.totalAmount,
          '마리 수익금 1%②': totals.mariShare,
          '계산서 총액①+②': totals.totalAmount + totals.mariShare,
          세금구분: '',
        }
      : { 합계: totals.totalAmount }),
    '배송형 지급(예정)일': '',
    '쿠폰형 지급(예정)일': '',
    분할내역: '',
  });

  const ws = XLSX.utils.json_to_sheet(sheetRows);
  finishSheet(ws);
  XLSX.utils.book_append_sheet(wb, ws, '정산합계');

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), fileName);
}
